import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const ThemeContext = createContext(null);
const defaultTheme = {
  primaryColor:   '#2563eb',
  secondaryColor: '#1e40af',
  sidebarColor:   '#1e293b',
  companyName:    'Backup MOC',
  logoUrl:        null,
};

function fromApi(row) {
  if (!row) return defaultTheme;
  return {
    primaryColor:   row.primary_color   || defaultTheme.primaryColor,
    secondaryColor: row.secondary_color || defaultTheme.secondaryColor,
    sidebarColor:   row.sidebar_color   || defaultTheme.sidebarColor,
    companyName:    row.company_name    || defaultTheme.companyName,
    logoUrl:        row.logo_url        || null,
  };
}

function toApi(theme) {
  return {
    company_name:    theme.companyName,
    primary_color:   theme.primaryColor,
    secondary_color: theme.secondaryColor,
    sidebar_color:   theme.sidebarColor,
    logo_url:        theme.logoUrl,
  };
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(defaultTheme);
  const [loaded, setLoaded] = useState(false);

  // Il branding è una configurazione GLOBALE salvata sul server, non più nel
  // localStorage del browser: deve essere identica per ogni utente, su
  // qualsiasi browser/dispositivo, non specifica della singola sessione
  // locale. L'endpoint GET è pubblico (nessuna autenticazione richiesta)
  // perché serve anche alla pagina di login, prima che l'utente sia
  // autenticato.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/settings')
      .then(res => { if (!cancelled) setTheme(fromApi(res.data)); })
      .catch(() => { /* in caso di errore si resta sul tema di default */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary',   theme.primaryColor);
    document.documentElement.style.setProperty('--color-secondary', theme.secondaryColor);
  }, [theme]);

  // updateTheme aggiorna lo stato locale (per riflettere subito le modifiche
  // nell'interfaccia, es. mentre l'admin modifica i colori) E persiste sul
  // server, così la modifica è visibile a tutti gli altri utenti/dispositivi
  // non appena ricaricano la pagina o effettuano un nuovo login.
  const updateTheme = async (updates) => {
    const next = { ...theme, ...updates };
    setTheme(next);
    try {
      await api.put('/api/settings', toApi(next));
    } catch (err) {
      // La modifica resta visibile localmente anche se il salvataggio sul
      // server fallisce (es. utente non admin, o errore di rete); chi
      // chiama updateTheme può comunque ispezionare l'errore se necessario.
      throw err;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, loaded }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
